import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Container, AlertTriangle, ChevronDown, Edit3, Plus, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';
import { CurrentAssignmentModal } from './CurrentAssignmentModal';

/**
 * Banner "Je conduis aujourd'hui" — affiche le camion + remorques courants
 * en haut du Dashboard chauffeur. Click → ouvre le modal de selection.
 */
export const CurrentAssignmentBanner = ({ onAssignmentChange }) => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState({ truck: null, trailers: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchAssignment = async () => {
    try {
      const r = await get('/driver/assignment/current');
      if (r.success) {
        setAssignment({ truck: r.data.truck, trailers: r.data.trailers || [] });
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignment();
  }, []);

  const handleSaved = (newAssignment) => {
    setAssignment({ truck: newAssignment.truck, trailers: newAssignment.trailers || [] });
    onAssignmentChange?.(newAssignment);
  };

  const truck = assignment.truck;
  const trailers = assignment.trailers || [];
  const needsTruckInsp = truck && truck.needs_inspection;
  const needsTrailerInsp = trailers.some((t) => t && t.needs_inspection);
  const hasAlert = needsTruckInsp || needsTrailerInsp;

  if (loading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 mb-4 animate-pulse">
        <div className="h-5 bg-slate-700 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`rounded-2xl p-4 mb-4 border-2 transition-colors cursor-pointer ${
          hasAlert
            ? 'bg-amber-500/10 border-amber-500/40 hover:border-amber-500/60'
            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
        }`}
        onClick={() => setModalOpen(true)}
        data-testid="assignment-banner"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Truck className={hasAlert ? 'text-amber-400' : 'text-amber-500'} size={20} />
            <h3 className="text-base font-bold text-white">Je conduis aujourd'hui</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {!truck && trailers.length === 0 ? (
              <span className="flex items-center gap-1 text-amber-400">
                <Plus size={14} /> À configurer
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Edit3 size={14} /> Modifier
              </span>
            )}
          </div>
        </div>

        {!truck && trailers.length === 0 ? (
          <p className="text-sm text-slate-300">
            Cliquez ici pour sélectionner votre <strong>camion</strong> et vos{' '}
            <strong>remorques</strong> du jour.
          </p>
        ) : (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            {truck && (
              <VehicleLine
                vehicle={truck}
                icon={Truck}
                label="Camion"
                onInspect={() => navigate('/inspection', { state: { vehicle: { ...truck, vehicle_type: 'truck' } } })}
              />
            )}
            {trailers.map((t, idx) => (
              <VehicleLine
                key={t?.id || idx}
                vehicle={t}
                icon={Container}
                label={trailers.length === 2 ? `Remorque ${idx + 1}` : 'Remorque'}
                onInspect={() => navigate('/inspection', { state: { vehicle: { ...t, vehicle_type: 'trailer' } } })}
              />
            ))}
          </div>
        )}

        {hasAlert && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-300 bg-amber-900/20 rounded-lg px-3 py-2">
            <AlertTriangle size={14} />
            <span>Inspection requise pour {needsTruckInsp && needsTrailerInsp ? 'le camion et les remorques modifiés' : needsTruckInsp ? 'le camion' : 'la/les remorque(s)'}</span>
          </div>
        )}
      </div>

      {modalOpen && (
        <CurrentAssignmentModal
          assignment={assignment}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            // Refetch from API to get the up-to-date assignment with inspection status
            fetchAssignment();
            setModalOpen(false);
            onAssignmentChange?.();
          }}
        />
      )}
    </>
  );
};

const VehicleLine = ({ vehicle, icon: Icon, label, onInspect }) => {
  if (!vehicle) return null;
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const tag = vehicle.unit_number
    ? `#${vehicle.unit_number}`
    : vehicle.plate_number || vehicle.id?.slice(0, 6);
  return (
    <div
      className="flex items-center gap-2 text-sm rounded-lg px-2 py-1 hover:bg-slate-700/30 cursor-pointer transition-colors"
      onClick={onInspect}
      data-testid={`assignment-vehicle-${vehicle.id}`}
      title="Cliquer pour inspecter ce véhicule"
    >
      <Icon size={16} className="text-slate-400 flex-shrink-0" />
      <span className="font-semibold text-white">{tag}</span>
      {title && <span className="text-slate-300">{title}</span>}
      {vehicle.plate_number && vehicle.unit_number && (
        <span className="text-xs text-slate-500">{vehicle.plate_number}</span>
      )}
      {vehicle.is_manual && (
        <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">Loué</span>
      )}
      {vehicle.needs_inspection ? (
        <span className="ml-auto text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <AlertTriangle size={11} />
          À inspecter
        </span>
      ) : (
        <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Check size={11} />
          Inspecté
        </span>
      )}
    </div>
  );
};
